import {useState} from 'react'
import {useQuery} from 'react-query'
import {fetchDriverLicenseStatusReport} from './api'
import {PageTitle} from '../../../_metronic/layout/core'
import CRUDTable from '../../../components/table/CRUDTable'
import toast, {Toaster} from 'react-hot-toast'
import viewProps from '../viewProps'
import {Error500} from '../../modules/errors/components/Error500'
import TopBarProgress from 'react-topbar-progress-indicator'
import {Processing} from '../../../components/Loaders'
import {Helmet} from 'react-helmet'
import Select from 'react-select'
import {DriverLicenseStatusReportBase, DriverLicenseStatusReportPayload} from './type'
import ExportToExcelButton from './ExportToExcelButton'
import {fetchDriverList} from '../driver/api'
import {mapFieldBasedOnCondition} from '../../../helpers/mapFieldBasedOnCondition'
import {APIResponse} from '../../../types/type'

const DriverLicenseStatusReport = () => {
  const [filters, setFilters] = useState<DriverLicenseStatusReportPayload>({
    driver_id: 'all',
  })

  const {data: driverList, isLoading: isLoadingDrivers} = useQuery('drivers', fetchDriverList, {
    refetchOnWindowFocus: false,
  })

  const {
    data: reportData,
    isLoading: isLoadingReport,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    'driver-license-status-report',
    () => fetchDriverLicenseStatusReport({driver_id: filters.driver_id.toString()}),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      initialData: {data: []} as Partial<APIResponse<DriverLicenseStatusReportBase[]>>,
      select: (response: any) => {
        const transformedData = response.data.map((driverData: any, index: number) => {
          const license_status = driverData.license_status

          let licenseStatusDesc = 'N/A'
          if (license_status !== null && license_status !== undefined) {
            if (license_status > 0) {
              licenseStatusDesc = `${license_status} days left`
            } else if (license_status === 0) {
              licenseStatusDesc = 'License Expired Today'
            } else {
              licenseStatusDesc = `Expired ${Math.abs(license_status)} days ago`
            }
          }

          return {
            ...driverData,
            sl_no: index + 1,
            license_status: licenseStatusDesc,
          }
        })

        return {
          ...response,
          data: transformedData,
        }
      },
    }
  )

  const handleApplyFilters = () => {
    const {driver_id} = filters

    if (!driver_id) {
      toast.error('Please select driver filter.')
      return
    }

    toast.loading('Applying filters...')
    refetch()
      .then(() => {
        toast.dismiss()
        toast.success('Filters applied successfully.')
      })
      .catch(() => {
        toast.dismiss()
        toast.error('Failed to fetch inspection report.')
      })
  }

  if (isLoadingDrivers) {
    return <Processing />
  }

  const driverOptions = [{driver_name: 'All Drivers', driver_id: 'all'}, ...driverList?.data!].map(
    (item: any) => ({
      label: item.driver_name.toUpperCase(),
      value: item.driver_id,
    })
  )

  const selectedDriverName =
    driverList?.data.find((opt: any) => opt.driver_id === filters.driver_id)?.driver_name ||
    'All DRIVERS'

  return (
    <>
      <Helmet>
        <title>TrackNAB | Inspection Report</title>
      </Helmet>
      <PageTitle>Inspection Report</PageTitle>

      {/* Filters Section */}
      <div className="mb-4 p-4 border rounded shadow-sm bg-light">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Driver</label>
            <Select
              options={driverOptions}
              onChange={(opt: any) => setFilters({...filters, driver_id: opt?.value || null})}
              value={driverOptions.find((opt) => opt.value === filters.driver_id) || null}
            />
          </div>
          <div className="col-md-3 align-self-end ms-4">
            <button className="btn btn-primary btn-sm mt-2" onClick={handleApplyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {isLoadingReport && <Processing />}
      {isError && <Error500 />}
      {reportData?.data.length! > 0 ? (
        <CRUDTable
          data={reportData?.data}
          {...viewProps.DriverLicenseStatus}
          fieldToLabelMap={viewProps.DriverLicenseStatus.primaryFieldsToLabelMap}
          headerExtras={
            <>
              <div className="ms-auto">
                <ExportToExcelButton
                  data={reportData?.data!}
                  fileName="Driver License Status Report"
                  heading={`TRANSPORT DEPARTMENT DRIVERS LICENSE STATUS - ${selectedDriverName}`}
                  titleMap={viewProps.DriverLicenseStatus.primaryFieldsToLabelMap}
                />
              </div>
            </>
          }
          // isSearchable={false}
        />
      ) : (
        <div className="fs-2 p-6 text-center">Apply filters / No data for applied filters</div>
      )}

      {isFetching && <TopBarProgress />}
      <Toaster />
    </>
  )
}

export default DriverLicenseStatusReport
